import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  EmailTemplateMacroList,
  EmailTemplateMacroPicker,
  type EmailTemplateMacroInsertTarget,
} from '../../components/Admin/EmailTemplateMacroList';
import { Button } from '../../components/UI/Button';
import { ConfirmModal } from '../../components/UI/ConfirmModal';
import { useToast } from '../../components/UI/ToastProvider';
import { emailTemplateService } from '../../services/emailTemplateService';
import {
  EMAIL_TEMPLATE_CATEGORIES,
  type EmailTemplate,
  type EmailTemplateMacro,
  type EmailTemplateTestPreview,
  type UpsertEmailTemplateRequest,
} from '../../types/emailTemplate';
import { extractEmailTemplateMacros } from '../../utils/emailTemplateMacros';
import { hasFilledMacroValues, renderEmailTemplate } from '../../utils/emailTemplateRenderer';

const PAGE_SIZE = 20;

const emailBodyEditorStyles = `
  .email-body-editor .ql-editor {
    min-height: 12rem;
    max-height: 20rem;
    overflow-y: auto;
  }
  .email-body-editor .ql-container {
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    font-size: 0.875rem;
  }
  .email-body-editor .ql-toolbar {
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }
`;

const emailBodyEditorModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const emailBodyReadOnlyModules = {
  toolbar: false,
  clipboard: {
    matchVisual: false,
  },
};

const emailBodyEditorFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'align',
  'link',
];

function macroValuesFromPreview(preview: EmailTemplateTestPreview): Record<string, string> {
  return preview.macros.reduce<Record<string, string>>((acc, macro) => {
    acc[macro.name] = '';
    return acc;
  }, {});
}

function isEmailBodyEmpty(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').trim().length === 0;
}

const emptyForm: UpsertEmailTemplateRequest & { id?: string; isSystem?: boolean } = {
  id: undefined,
  name: '',
  codeName: '',
  description: '',
  category: EMAIL_TEMPLATE_CATEGORIES[0],
  subject: '',
  bodyHtml: '',
  isActive: true,
  isSystem: false,
};

function formatDate(iso: string | null): string {
  if (!iso) {
    return '-';
  }
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export const EmailTemplatesPage: React.FC = () => {
  const { addToast } = useToast();

  const [items, setItems] = useState<EmailTemplate[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);

  const [preview, setPreview] = useState<EmailTemplate | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; item: EmailTemplate | null }>({
    open: false,
    item: null,
  });

  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testPanelOpen, setTestPanelOpen] = useState(false);
  const [testPanelLoading, setTestPanelLoading] = useState(false);
  const [testPanelSending, setTestPanelSending] = useState(false);
  const [testPreview, setTestPreview] = useState<EmailTemplateTestPreview | null>(null);
  const [testPanelEmail, setTestPanelEmail] = useState('');
  const [macroValues, setMacroValues] = useState<Record<string, string>>({});
  const [macroCatalog, setMacroCatalog] = useState<EmailTemplateMacro[]>([]);
  const [macrosLoading, setMacrosLoading] = useState(false);
  const bodyEditorRef = useRef<ReactQuill>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);

   const usedTemplateMacros = useMemo(
      () => extractEmailTemplateMacros(form.subject, form.bodyHtml),
      [form.subject, form.bodyHtml]
  );

  const renderedTestSubject = useMemo(() => {
    if (!testPreview) {
      return '';
    }

    if (!hasFilledMacroValues(testPreview.macros, macroValues)) {
      return testPreview.renderedSubject;
    }

    return renderEmailTemplate(testPreview.template.subject, macroValues);
  }, [testPreview, macroValues]);

  const insertTemplateMacro = useCallback(
    (macro: string, target: EmailTemplateMacroInsertTarget) => {
      if (target === 'subject')
      {
        const input = subjectInputRef.current;
        const currentSubject = form.subject;
        const start = input?.selectionStart ?? currentSubject.length;
        const end = input?.selectionEnd ?? start;
        const nextSubject = `${currentSubject.slice(0, start)}${macro}${currentSubject.slice(end)}`;

        setForm((current) => ({ ...current, subject: nextSubject }));

        if (input)
        {
          requestAnimationFrame(() => {
            input.focus();
            const cursor = start + macro.length;
            input.setSelectionRange(cursor, cursor);
          });
        }

        return;
      }

      const editor = bodyEditorRef.current?.getEditor();
      if (!editor)
      {
        setForm((current) => ({ ...current, bodyHtml: `${current.bodyHtml}${macro}` }));
        return;
      }

      const range = editor.getSelection(true);
      editor.insertText(range.index, macro);
      editor.setSelection(range.index + macro.length, 0);
    },
    [form.subject]
  );

  const renderedTestBodyHtml = useMemo(() => {
    if (!testPreview) {
      return '';
    }

    if (!hasFilledMacroValues(testPreview.macros, macroValues)) {
      return testPreview.renderedBodyHtml;
    }

    return renderEmailTemplate(testPreview.template.bodyHtml, macroValues);
  }, [testPreview, macroValues]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await emailTemplateService.getAll({
        search: search || undefined,
        category: category || undefined,
        isActive: statusFilter === '' ? undefined : statusFilter === 'active',
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
    } catch {
      addToast('Erro ao carregar templates de e-mail.', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, category, statusFilter, page, addToast]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;

    const loadMacros = async () => {
      setMacrosLoading(true);
      try
      {
        const macros = await emailTemplateService.getMacros();
        if (!cancelled)
        {
          setMacroCatalog(macros);
        }
      }
      catch
      {
        if (!cancelled)
        {
          addToast('Erro ao carregar variáveis de template.', 'error');
        }
      }
      finally
      {
        if (!cancelled)
        {
          setMacrosLoading(false);
        }
      }
    };

    void loadMacros();

    return () => {
      cancelled = true;
    };
  }, [addToast]);

  const openCreate = () => {
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (item: EmailTemplate) => {
    setForm({
      id: item.id,
      name: item.name,
      codeName: item.codeName,
      description: item.description ?? '',
      category: item.category,
      subject: item.subject,
      bodyHtml: item.bodyHtml,
      isActive: item.isActive,
      isSystem: item.isSystem,
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEmailBodyEmpty(form.bodyHtml))
    {
      addToast('O corpo do e-mail é obrigatório.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload: UpsertEmailTemplateRequest = {
        name: form.name.trim(),
        codeName: form.codeName.trim().toUpperCase(),
        description: form.description?.trim() || null,
        category: form.category,
        subject: form.subject,
        bodyHtml: form.bodyHtml,
        isActive: form.isActive,
      };

      if (form.id) {
        await emailTemplateService.update(form.id, payload);
        addToast('Template atualizado com sucesso.', 'success');
      } else {
        await emailTemplateService.create(payload);
        addToast('Template criado com sucesso.', 'success');
      }

      setShowForm(false);
      await fetchData();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Erro ao salvar o template.';
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    const item = confirmDelete.item;
    setConfirmDelete({ open: false, item: null });
    if (!item) {
      return;
    }
    try {
      await emailTemplateService.remove(item.id);
      addToast('Template excluído com sucesso.', 'success');
      await fetchData();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Não foi possível excluir o template.';
      addToast(msg, 'error');
    }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim()) {
      addToast('Informe um e-mail para o teste.', 'error');
      return;
    }
    setSendingTest(true);
    try {
      await emailTemplateService.sendTest(testEmail.trim());
      addToast('E-mail de teste enfileirado com sucesso.', 'success');
      setTestEmail('');
    } catch {
      addToast('Falha ao enviar e-mail de teste.', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  const closeTestPanel = () => {
    setTestPanelOpen(false);
    setTestPreview(null);
    setMacroValues({});
    setTestPanelEmail('');
  };

  const openTestPanel = async (item: EmailTemplate) => {
    setTestPanelOpen(true);
    setTestPanelLoading(true);
    setTestPreview(null);
    setMacroValues({});
    setTestPanelEmail(testEmail.trim());

    try {
      const previewData = await emailTemplateService.getTestPreview(item.id);
      setTestPreview(previewData);
      setMacroValues(macroValuesFromPreview(previewData));
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        `Falha ao carregar o teste do template "${item.name}".`;
      addToast(msg, 'error');
      closeTestPanel();
    } finally {
      setTestPanelLoading(false);
    }
  };

  const handleSendTemplateTest = async () => {
    if (!testPreview) {
      return;
    }

    if (!testPanelEmail.trim()) {
      addToast('Informe um e-mail de destino para o teste.', 'error');
      return;
    }

    const emptyMacro = testPreview.macros.find((macro) => !macroValues[macro.name]?.trim());
    if (emptyMacro) {
      addToast(`Preencha o valor da macro {{${emptyMacro.name}}}.`, 'error');
      return;
    }

    setTestPanelSending(true);
    try {
      await emailTemplateService.sendTemplateTest(testPreview.template.id, {
        to: testPanelEmail.trim(),
        context: macroValues,
      });
      addToast(`Template "${testPreview.template.name}" enfileirado para envio de teste.`, 'success');
      closeTestPanel();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        `Falha ao enviar o teste do template "${testPreview.template.name}".`;
      addToast(msg, 'error');
    } finally {
      setTestPanelSending(false);
    }
  };

  return (
    <div className="p-2">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates de E-mail</h1>
          <p className="text-sm text-gray-500">
            Gerencie os templates usados em todos os disparos de e-mail do sistema.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="E-mail para teste SMTP"
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <Button variant="secondary" size="sm" onClick={() => void handleSendTest()} disabled={sendingTest}>
            {sendingTest ? 'Enviando...' : 'Testar SMTP'}
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            Novo template
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="mb-1 block text-xs font-medium text-gray-600">Buscar</label>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Nome, código ou assunto"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Categoria</label>
          <select
            value={category}
            onChange={(e) => {
              setPage(1);
              setCategory(e.target.value);
            }}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Todas</option>
            {EMAIL_TEMPLATE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value);
            }}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Todos</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Nome</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Código</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Categoria</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Atualizado</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  Nenhum template encontrado.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{item.name}</div>
                    {item.isSystem && (
                      <span className="mt-1 inline-block rounded bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                        Sistema
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <code className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{item.codeName}</code>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.category}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
                        item.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {item.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(item.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => void openTestPanel(item)}
                        title="Testar template"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setPreview(item)} title="Pré-visualizar">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => openEdit(item)} title="Editar">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setConfirmDelete({ open: true, item })}
                        disabled={item.isSystem}
                        title={item.isSystem ? 'Templates de sistema não podem ser excluídos' : 'Excluir'}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <span>
          {totalCount} template(s) — página {page} de {Math.max(totalPages, 1)}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próximo
          </Button>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {form.id ? 'Editar template' : 'Novo template'}
              </h2>
              <EmailTemplateMacroPicker
                catalog={macroCatalog}
                loading={macrosLoading}
                onInsert={insertTemplateMacro}
              />
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Nome</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="w-full rounded border border-gray-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">CodeName</label>
                  <input
                    type="text"
                    value={form.codeName}
                    onChange={(e) => setForm({ ...form, codeName: e.target.value.toUpperCase() })}
                    required
                    readOnly={Boolean(form.id)}
                    placeholder="EX: USER_WELCOME"
                    className={`w-full rounded border border-gray-300 px-3 py-2 ${
                      form.id ? 'bg-gray-100 text-gray-500' : ''
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Categoria</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full rounded border border-gray-300 px-3 py-2"
                  >
                    {EMAIL_TEMPLATE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                    Ativo
                  </label>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                <input
                  type="text"
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Assunto</label>
                <input
                  ref={subjectInputRef}
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  required
                  className="w-full rounded border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Corpo do e-mail <span className="text-xs text-gray-400">(use {'{{MACRO}}'} para variáveis)</span>
                </label>
                <style>{emailBodyEditorStyles}</style>
                <div className="email-body-editor rounded border border-gray-300">
                  <ReactQuill
                    ref={bodyEditorRef}
                    key={form.id ?? 'new'}
                    theme="snow"
                    value={form.bodyHtml}
                    onChange={(bodyHtml) => setForm((current) => ({ ...current, bodyHtml }))}
                    modules={emailBodyEditorModules}
                    formats={emailBodyEditorFormats}
                  />
                </div>
              </div>

              <EmailTemplateMacroList
                catalog={macroCatalog}
                usedMacros={usedTemplateMacros}
              />

              <div className="flex justify-end gap-2 border-t pt-4">
                <Button variant="secondary" size="sm" type="button" onClick={() => setShowForm(false)}>
                  Cancelar
                </Button>
                <Button variant="primary" size="sm" type="submit" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Pré-visualização</h2>
              <Button variant="secondary" size="sm" onClick={() => setPreview(null)}>
                Fechar
              </Button>
            </div>
            <p className="mb-2 text-sm text-gray-500">
              Assunto: <span className="font-medium text-gray-800">{preview.subject}</span>
            </p>
            <div
              className="rounded border border-gray-200 p-4"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: preview.bodyHtml }}
            />
          </div>
        </div>
      )}

      {testPanelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Testar template</h2>

            {testPanelLoading && (
              <p className="py-8 text-center text-sm text-gray-500">Carregando preview do template...</p>
            )}

            {!testPanelLoading && testPreview && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Nome</label>
                    <input
                      type="text"
                      value={testPreview.template.name}
                      readOnly
                      className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">CodeName</label>
                    <input
                      type="text"
                      value={testPreview.template.codeName}
                      readOnly
                      className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Categoria</label>
                    <input
                      type="text"
                      value={testPreview.template.category}
                      readOnly
                      className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input type="checkbox" checked={testPreview.template.isActive} readOnly disabled />
                      Ativo
                    </label>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                  <input
                    type="text"
                    value={testPreview.template.description ?? ''}
                    readOnly
                    className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Assunto (template)</label>
                  <input
                    type="text"
                    value={testPreview.template.subject}
                    readOnly
                    className="w-full rounded border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Corpo do e-mail (template)
                  </label>
                  <style>{emailBodyEditorStyles}</style>
                  <div className="email-body-editor rounded border border-gray-300">
                    <ReactQuill
                      key={testPreview.template.id}
                      theme="snow"
                      value={testPreview.template.bodyHtml}
                      readOnly
                      modules={emailBodyReadOnlyModules}
                      formats={emailBodyEditorFormats}
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="mb-1 text-sm font-semibold text-gray-900">Variáveis detectadas</h3>
                  <p className="mb-3 text-xs text-gray-500">
                    Preencha os valores que substituirão as macros do template salvo no banco.
                  </p>
                  {testPreview.macros.length === 0 && (
                    <p className="text-sm text-gray-500">Nenhuma macro {'{{MACRO}}'} encontrada neste template.</p>
                  )}
                  {testPreview.macros.length > 0 && (
                    <div className="space-y-3">
                      {testPreview.macros.map((macro) => (
                        <div key={macro.name} className="grid grid-cols-1 gap-2 md:grid-cols-[160px_1fr] md:items-center">
                          <code className="rounded bg-white px-2 py-1 text-xs text-gray-700">{`{{${macro.name}}}`}</code>
                          <input
                            type="text"
                            value={macroValues[macro.name] ?? ''}
                            onChange={(e) =>
                              setMacroValues((current) => ({
                                ...current,
                                [macro.name]: e.target.value,
                              }))
                            }
                            placeholder={`Valor para {{${macro.name}}}`}
                            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <h3 className="mb-3 text-sm font-semibold text-blue-900">Pré-visualização renderizada</h3>
                  <p className="mb-3 text-sm text-blue-900">
                    Assunto: <span className="font-medium">{renderedTestSubject || '(vazio)'}</span>
                  </p>
                  <div
                    className="rounded border border-blue-100 bg-white p-4 text-sm text-gray-800"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: renderedTestBodyHtml || '<p class="text-gray-400">(vazio)</p>' }}
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">E-mail de destino</label>
                  <input
                    type="email"
                    value={testPanelEmail}
                    onChange={(e) => setTestPanelEmail(e.target.value)}
                    placeholder="exemplo@empresa.com"
                    className="w-full rounded border border-gray-300 px-3 py-2"
                  />
                </div>

                <div className="flex justify-end gap-2 border-t pt-4">
                  <Button variant="secondary" size="sm" type="button" onClick={closeTestPanel}>
                    Cancelar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    type="button"
                    onClick={() => void handleSendTemplateTest()}
                    disabled={testPanelSending}
                  >
                    {testPanelSending ? 'Enviando...' : 'Enviar teste'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.open}
        title="Confirmar exclusão"
        message={`Tem certeza que deseja excluir o template "${confirmDelete.item?.name}"? Esta ação não poderá ser desfeita.`}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete({ open: false, item: null })}
        confirmText="Excluir"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default EmailTemplatesPage;
